import UpvoteButton from "@shared/components/UpvoteButton";
import { processHtmlContent } from "@shared/utils/htmlUtils";
import theme from "@shared/utils/themeColors";
import { Lock } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import SpaceMetadata from "./SpaceMetadata";
import { PollDisplay } from "@features/polls";

import { LoadingSpinner } from "@shared/components/LoadingSpinner";
import { useProfile } from "@shared/hooks/useProfile";
import { useSpacesStore } from "../store/useSpacesStore";
import CommentForm from "./CommentForm";
import CommentItem from "./CommentItem";

function SpaceEntry() {
  let loaded = false;
  const { spaceId } = useParams<{ spaceId: string }>();
  const { profile } = useProfile();
  const {
    currentSpace,
    spaceComments,
    isLoading,
    isLoadingComments,
    loadingComments,
    fetchSpace,
    fetchSpaceComments,
    toggleUpvote,
    toggleFollow,
    addComment,
    setSpacePoll,
  } = useSpacesStore();

  const [commentContent, setCommentContent] = useState("");
  const [replyTo, setReplyTo] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // For comment pagination
  const [visibleComments, setVisibleComments] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (spaceId && !loaded) {
      fetchSpace(spaceId);
      fetchSpaceComments(spaceId);
      loaded = true;
    }
  }, [spaceId, fetchSpace, fetchSpaceComments]);

  // Process HTML content with useMemo to apply styles
  const processedContent = useMemo(() => {
    return processHtmlContent(currentSpace?.content || "");
  }, [currentSpace?.content]);

  // Get all available users for mentions from the comments
  const availableUsers = useMemo(() => {
    // Get all unique users who have commented
    const commenters = spaceComments
      .map((comment) => comment.author)
      .filter(
        (author, index, self) =>
          index === self.findIndex((a) => a.id === author.id),
      );

    // Add current space author if available
    if (currentSpace?.author && currentSpace.author.id) {
      const authorExists = commenters.some(
        (user) => user.id === currentSpace.author.id,
      );
      if (!authorExists) {
        commenters.push(currentSpace.author);
      }
    }

    return commenters;
  }, [spaceComments, currentSpace]);

  const handleCommentSubmit = async () => {
    if (!spaceId || !commentContent.trim()) return;

    try {
      setIsSubmitting(true);
      const newComment = await addComment(spaceId, commentContent, replyTo);

      if (newComment) {
        setCommentContent("");
        setReplyTo(undefined);

        // Scroll to the new comment after a short delay to ensure it's rendered
        if (newComment.id) {
          setTimeout(() => {
            const commentElement = document.getElementById(
              `comment-${newComment.id}`,
            );
            if (commentElement) {
              commentElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              // Add a brief highlight effect
              // Add theme.bg.accent class
              commentElement.classList.add(theme.bg.accent);
              setTimeout(() => {
                // Remove theme.bg.accent class
                commentElement.classList.remove(theme.bg.accent);
              }, 2000);
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle mentions in comments
  const handleMention = (username: string) => {
    setCommentContent((prev) => {
      const mentionText = `@${username} `;
      return prev.includes(`@${username}`) ? prev : `${prev}${mentionText}`;
    });
  };

  // Load more comments
  const loadMoreComments = (parentId: string = null) => {
    setVisibleComments((prev) => ({
      ...prev,
      [parentId]: (prev[parentId] || 3) + 3,
    }));
  };

  // Filter comments by parent ID
  const getCommentsByParentId = (parentId?: string) => {
    // For root comments (null parentId), we need to check both null and undefined
    if (parentId === undefined) {
      return spaceComments.filter(
        (comment) =>
          comment.parentId === null || comment.parentId === undefined,
      );
    }
    // For replies, filter by the specific parentId
    return spaceComments.filter((comment) => comment.parentId === parentId);
  };

  // Get limited comments for display
  const getLimitedComments = (parentId?: string) => {
    const comments = getCommentsByParentId(parentId);
    const limit = visibleComments[parentId || null] || 10;
    return comments.slice(0, limit);
  };

  // Check if there are more comments to load
  const hasMoreComments = (parentId?: string) => {
    const comments = getCommentsByParentId(parentId);
    const limit = visibleComments[parentId || null] || 10;
    return comments.length > limit;
  };

  const handleUpvote = () => {
    if (spaceId) {
      toggleUpvote(spaceId);
    }
  };

  const handleFollow = () => {
    if (spaceId) {
      toggleFollow(spaceId);
    }
  };

  return (
    <div className="mb-6">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : currentSpace ? (
        <article className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-5">
          {/* Title with Upvote button */}
          <div className="flex items-start mb-3 justify-between">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 font-madefor flex items-center gap-2">
              {currentSpace.title}
              {currentSpace.isPrivate && (
                <Lock size={18} className="text-violet-500 shrink-0" />
              )}
            </h1>
            <UpvoteButton
              count={currentSpace.upvotes || 0}
              isUpvoted={currentSpace.isUpvoted}
              onClick={() => currentSpace.id && toggleUpvote(currentSpace.id)}
            />
          </div>

          {/* Meta */}
          <SpaceMetadata
            space={currentSpace}
            onFollow={handleFollow}
            className="mb-6"
            fullView={true}
          />

          {/* Content */}
          <div className="space-y-4">
            <div className="text-sm text-gray-800 dark:text-gray-200 space-y-4 break-words min-w-0">
              {/* Process and render content as HTML with enhanced styling */}
              <div
                dangerouslySetInnerHTML={{
                  __html: processedContent,
                }}
              />
            </div>

            {/* Poll content */}
            {currentSpace.poll && (
              <PollDisplay
                poll={currentSpace.poll}
                ownerType="space"
                onPollChange={(poll) => setSpacePoll(currentSpace.id!, poll)}
              />
            )}

            {/* Media content */}
            {currentSpace.media && currentSpace.media.length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentSpace.media.map((item, index) => {
                  const mainVariant =
                    item.variants.find((v) => v.type === "original") ||
                    item.variants[0];
                  return (
                    <div
                      key={item.id || index}
                      className="relative rounded-lg overflow-hidden"
                    >
                      {item.type === "image" ? (
                        <img
                          src={mainVariant.url}
                          alt=""
                          className="w-full h-auto object-cover"
                          width={mainVariant.width}
                          height={mainVariant.height}
                        />
                      ) : (
                        <video
                          src={mainVariant.url}
                          controls
                          className="w-full h-auto"
                          width={mainVariant.width}
                          height={mainVariant.height}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="mt-8">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4 font-madefor">
              Comments
            </h3>

            {/* Comment form */}
            <div className="mb-8">
              <CommentForm
                profile={profile}
                commentContent={commentContent}
                setCommentContent={setCommentContent}
                onSubmit={handleCommentSubmit}
                isSubmitting={isSubmitting || loadingComments["root"]}
                onCancel={replyTo ? () => setReplyTo(undefined) : undefined}
                availableUsers={availableUsers}
              />
            </div>

            {/* Comments body */}
            <div>
              {isLoadingComments ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner />
                </div>
              ) : spaceComments.length > 0 ? (
                <ul className="space-y-5">
                  {/* Root level comments with pagination */}
                  {getCommentsByParentId(undefined).map((comment) => (
                    <div
                      key={comment.id}
                      id={`comment-${comment.id}`}
                      className="transition-colors duration-500"
                    >
                      <CommentItem
                        comment={comment}
                        onReply={setReplyTo}
                        isReplyActive={replyTo === comment.id}
                        onMention={handleMention}
                        getCommentsByParentId={getCommentsByParentId}
                        getLimitedComments={getLimitedComments}
                        hasMoreComments={hasMoreComments}
                        loadMoreComments={loadMoreComments}
                      />
                    </div>
                  ))}

                  {/* Load more root comments */}
                  {hasMoreComments() && (
                    <div className="text-center mt-5">
                      <button
                        className={`text-sm font-medium ${theme.text.brand} ${theme.text.brandHover}`}
                        onClick={() => loadMoreComments()}
                      >
                        View More Comments
                      </button>
                    </div>
                  )}
                </ul>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No comments yet. Be the first to comment!
                </div>
              )}
            </div>
          </div>
        </article>
      ) : (
        <div className="text-center py-8 text-gray-500">
          Space not found or has been deleted.
        </div>
      )}
    </div>
  );
}

export default SpaceEntry;
